import { useState } from "react";
import { api, type WindowInfo } from "../lib/api";
import { brandColor, windowState, type GroupedEvent } from "../lib/events";
import { relativeTime, shortDate, windowLabel } from "../lib/format";
import { Link } from "../lib/router";
import { useApp } from "../lib/store";
import { LiveBadge } from "./bits";

interface CardProps {
  group: GroupedEvent;
  /** the region tab currently active — decides which windows we surface */
  region: string;
  isTracked: boolean;
  storedCount: number;
  index?: number;
}

/** Follow/unfollow an event family (writes the durable watchlist). */
export function TrackToggle({
  baseId,
  isTracked,
  size = "sm",
}: {
  baseId: string;
  isTracked: boolean;
  size?: "sm" | "md";
}) {
  const { refreshStatus } = useApp();
  const [busy, setBusy] = useState(false);

  const toggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (isTracked) await api.untrack(baseId);
      else await api.track(baseId);
      await refreshStatus();
    } catch {
      // status chip in the nav already tells the offline story
    } finally {
      setBusy(false);
    }
  };

  const pad = size === "md" ? "px-3.5 py-1.5 text-xs" : "px-2.5 py-1 text-[11px]";
  return (
    <button
      onClick={toggle}
      disabled={busy}
      className={`chamfer-sm font-mono font-medium uppercase tracking-wider transition ${pad} ${
        isTracked
          ? "bg-accent/15 text-accent hover:bg-accent/25"
          : "bg-raised text-dim hover:bg-overlay hover:text-fg"
      } ${busy ? "opacity-60" : ""}`}
    >
      {isTracked ? "tracking" : "track"}
    </button>
  );
}

export function EventCard({ group, region, isTracked, storedCount, index = 0 }: CardProps) {
  const { admin } = useApp();
  const color = brandColor(group.display, "#2e2e3e");
  const tile = group.display?.tile_image;
  const regional = group.regions.find(r => r.region === region);
  const next = regional?.windows
    .map(w => Date.parse(w.begin_time))
    .filter(t => !Number.isNaN(t) && t > Date.now())
    .sort((a, b) => a - b)[0];

  return (
    <Link to={`/event/${encodeURIComponent(group.base_id)}`} className="group block">
      <span
        className="fade-up press-card chamfer block overflow-hidden bg-surface group-hover:-translate-y-0.5"
        style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
      >
        <span className="relative block aspect-[16/8] overflow-hidden">
          {tile ? (
            <img
              src={tile}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover saturate-[0.85] transition duration-300 group-hover:scale-[1.02] group-hover:saturate-100"
            />
          ) : (
            <span
              className="block h-full w-full"
              style={{
                background: `linear-gradient(130deg, ${color}55, ${color}14 55%, #101016)`,
              }}
            />
          )}
          <span className="absolute inset-0 bg-gradient-to-t from-surface via-surface/15 to-transparent" />
          {group.state === "live" && <LiveBadge className="absolute left-3 top-3" />}
          {group.state !== "live" && next && (
            <span className="chamfer-sm absolute left-3 top-3 bg-bg/80 px-2 py-0.5 font-mono text-[10.5px] text-dim backdrop-blur-sm">
              {relativeTime(new Date(next).toISOString())}
            </span>
          )}
        </span>

        <span className="block px-4 pb-4 pt-1.5">
          <span className="block truncate text-[14.5px] font-semibold leading-snug text-fg">
            {group.title}
          </span>
          <span className="mt-2 flex items-center justify-between gap-3">
            <span className="font-mono text-[11px] text-faint">
              {storedCount > 0
                ? `${storedCount} ${storedCount === 1 ? "lobby" : "lobbies"} captured`
                : (regional?.windows.length ?? 0) > 0
                  ? `${regional!.windows.length} windows`
                  : "no windows in " + region}
            </span>
            {admin && <TrackToggle baseId={group.base_id} isTracked={isTracked} />}
          </span>
        </span>
      </span>
    </Link>
  );
}

/** Wide spotlight card for an event that is live right now. */
export function LiveEventCard({
  group,
  region,
  isTracked,
  watchedWindows,
}: {
  group: GroupedEvent;
  region: string;
  isTracked: boolean;
  watchedWindows: WindowInfo[];
}) {
  const { status, admin } = useApp();
  const art = group.display?.tile_image;
  const color = brandColor(group.display, "#2e2e3e");
  const regional = group.regions.find(r => r.region === region);
  const liveWindows =
    regional?.windows.filter(w => windowState(w, Date.now()) === "live") ?? [];

  const windowIds = new Set(watchedWindows.map(w => w.event_window_id));
  const tailing =
    status?.matches.filter(m => windowIds.has(m.event_window_id) && m.status === "watching")
      .length ?? 0;
  const seen = watchedWindows.reduce(
    (n, w) => n + w.discovered + w.watching + w.done + w.failed,
    0,
  );

  return (
    <div className="fade-up chamfer live-glow overflow-hidden bg-surface">
      <div className="flex flex-col sm:flex-row">
        <Link
          to={`/event/${encodeURIComponent(group.base_id)}`}
          className="relative block shrink-0 overflow-hidden sm:w-[280px]"
        >
          {art ? (
            <img
              src={art}
              alt=""
              loading="lazy"
              className="h-36 w-full object-cover sm:h-full"
            />
          ) : (
            <span
              className="block h-36 w-full sm:h-full"
              style={{ background: `linear-gradient(130deg, ${color}55, #101016)` }}
            />
          )}
          <span className="absolute inset-0 bg-gradient-to-t from-surface/80 to-transparent sm:bg-gradient-to-r" />
        </Link>

        <div className="flex min-w-0 flex-1 flex-col justify-between gap-4 p-5">
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <LiveBadge />
              {admin && <TrackToggle baseId={group.base_id} isTracked={isTracked} />}
            </div>
            <Link
              to={`/event/${encodeURIComponent(group.base_id)}`}
              className="display mt-2.5 block text-xl leading-snug text-fg transition hover:text-accent-bright"
            >
              {group.title}
            </Link>
            {liveWindows.length > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-2">
                {liveWindows.map(w => (
                  <Link
                    key={w.event_window_id}
                    to={`/window/${encodeURIComponent(w.event_window_id)}`}
                    className="chamfer-sm bg-accent/10 px-2.5 py-1 font-mono text-[11px] text-accent transition hover:bg-accent/20"
                  >
                    {windowLabel(w.event_window_id, regional?.event_id ?? "")} →
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-end justify-between gap-4">
            <div className="flex gap-5">
              {admin && <Stat value={tailing} label="tailing" accent={tailing > 0} />}
              {admin && <Stat value={seen} label="sessions seen" />}
              <Stat
                value={liveWindows[0] ? shortDate(liveWindows[0].end_time) : "—"}
                label="window ends"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({
  value,
  label,
  accent = false,
}: {
  value: number | string;
  label: string;
  accent?: boolean;
}) {
  return (
    <span className="block">
      <span
        className={`block font-mono text-[14px] font-semibold ${accent ? "text-accent" : "text-fg"}`}
      >
        {value}
      </span>
      <span className="label-xs block">{label}</span>
    </span>
  );
}
